import { Practitioner, PractitionerMedicalService } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { createMedicalServiceSettlementTypeVisitor } from '../settlement/settlement.utils';
import { MedicalServiceFormBean } from '../form/medicalServiceForm.types';

import { MedicalServicePractitionerBean } from './practitioner.types';

export const mapPractitionerToMedicalServicePractitionerBean = (formData: MedicalServiceFormBean) => (
   practitioner: Practitioner
): MedicalServicePractitionerBean => {
   const basicData = {
      id: practitioner.id,
      displayName: `${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`,
      specialties: practitioner.specialtyNames,
      usingDefaultConfiguration: true,
      taxRate: formData.taxRate,
      individualPrice: formData.defaultPrice,
   } as const;

   return createMedicalServiceSettlementTypeVisitor(formData.settlementType)<MedicalServicePractitionerBean>({
      visitNone: () => ({
         ...basicData,
         settlementType: 'None',
      }),
      visitFixed: () => ({
         ...basicData,
         settlementType: 'Fixed',
         fixedValue: formData.fixedValue!,
      }),
      visitProportional: () => ({
         ...basicData,
         settlementType: 'Proportional',
         proportionalValue: formData.proportionalValue!,
      }),
   });
};

export const mapPractitionerMedicalServiceToMedicalServicePractitionerBean = (formData: MedicalServiceFormBean) => ({
   practitioner,
   usingDefaultConfiguration,
   individualPrice,
   settlementConfiguration,
}: PractitionerMedicalService): MedicalServicePractitionerBean => {
   const basicData = {
      id: practitioner.id,
      displayName: `${practitioner.firstName.asString()} ${practitioner.lastName.asString()}`,
      specialties: practitioner.specialtyNames,
      usingDefaultConfiguration: usingDefaultConfiguration,
      taxRate: formData.taxRate,
      individualPrice: individualPrice?.asNumber() ?? formData.defaultPrice,
   } as const;

   if (!settlementConfiguration) {
      return createMedicalServiceSettlementTypeVisitor(formData.settlementType)<MedicalServicePractitionerBean>({
         visitNone: () => ({
            ...basicData,
            settlementType: 'None',
         }),
         visitFixed: () => ({
            ...basicData,
            settlementType: 'Fixed',
            fixedValue: formData.fixedValue!,
         }),
         visitProportional: () => ({
            ...basicData,
            settlementType: 'Proportional',
            proportionalValue: formData.proportionalValue!,
         }),
      });
   }

   return createMedicalServiceSettlementTypeVisitor(
      settlementConfiguration.settlementType
   )<MedicalServicePractitionerBean>({
      visitNone: () => ({
         ...basicData,
         settlementType: 'None',
      }),
      visitFixed: () => ({
         ...basicData,
         settlementType: 'Fixed',
         fixedValue: settlementConfiguration.fixedValue!.asNumber(),
      }),
      visitProportional: () => ({
         ...basicData,
         settlementType: 'Proportional',
         proportionalValue: settlementConfiguration.proportionalValue!,
      }),
   });
};

export const updatePractitionerWithDefaultConfiguration = (formData: MedicalServiceFormBean) => (
   practitioner: MedicalServicePractitionerBean
): MedicalServicePractitionerBean => {
   if (practitioner.usingDefaultConfiguration) {
      const basicData = {
         id: practitioner.id,
         displayName: practitioner.displayName,
         specialties: practitioner.specialties,
         usingDefaultConfiguration: true,
         taxRate: formData.taxRate,
         individualPrice: formData.defaultPrice,
      } as const;

      return createMedicalServiceSettlementTypeVisitor(formData.settlementType)<MedicalServicePractitionerBean>({
         visitNone: () => ({
            ...basicData,
            settlementType: 'None',
         }),
         visitFixed: () => ({
            ...basicData,
            settlementType: 'Fixed',
            fixedValue: formData.fixedValue!,
         }),
         visitProportional: () => ({
            ...basicData,
            settlementType: 'Proportional',
            proportionalValue: formData.proportionalValue!,
         }),
      });
   }

   return {
      ...practitioner,
      taxRate: formData.taxRate,
   };
};
